import { useCallback, useMemo, useState } from "react";

import { MultiSelectDropdown } from "#components/MultiSelectDropdown";
import { MultiSelectField, type MultiSelectFieldItem } from "#components/MultiSelectField";

import type {
  MultiSelectComboBoxBaseProps,
  MultiSelectComboBoxOption,
} from "./MultiSelectComboBox.types";

export type MultiSelectComboBoxProps = MultiSelectComboBoxBaseProps;

/**
 * MultiSelectComboBox - A compound component combining MultiSelectField and MultiSelectDropdown.
 *
 * Provides a ready-to-use multi-select dropdown with built-in state management for
 * open/close, search filtering, selected value display as tags, and tag removal.
 *
 * Supports both **uncontrolled** (manages its own selection) and **controlled** modes.
 *
 * @example Uncontrolled (simplest usage)
 * ```tsx
 * <MultiSelectComboBox
 *   label="Providers"
 *   placeholder="Select providers..."
 *   options={[
 *     { id: "aws", name: "AWS" },
 *     { id: "gcp", name: "GCP" },
 *   ]}
 *   onChange={(selected) => console.log("selected:", selected)}
 * />
 * ```
 *
 * @example Uncontrolled with initial values
 * ```tsx
 * <MultiSelectComboBox
 *   label="Providers"
 *   options={options}
 *   defaultValue={[{ id: "aws", name: "AWS" }, { id: "gcp", name: "GCP" }]}
 * />
 * ```
 *
 * @example Controlled
 * ```tsx
 * <MultiSelectComboBox
 *   label="Providers"
 *   options={providers}
 *   value={selectedOptions}
 *   onChange={setSelectedOptions}
 *   withSearch
 * />
 * ```
 */
export function MultiSelectComboBox({
  options,
  value: controlledValue,
  defaultValue,
  onChange,
  label,
  placeholder = "Select options...",
  tooltipText,
  assistiveText,
  isDisabled,
  isReadOnly,
  isInvalid,
  isRequired,
  withSearch = false,
  searchPlaceholder,
  onSearch: externalSearch,
  placement = "bottom",
  className,
  showClearAll = false,
  ref,
}: MultiSelectComboBoxProps) {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState<MultiSelectComboBoxOption[]>(
    defaultValue ?? [],
  );
  const selectedOptions = isControlled ? controlledValue : internalValue;

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const selectedItems: MultiSelectFieldItem[] = useMemo(
    () =>
      selectedOptions.map((opt) => ({
        id: opt.id,
        name: opt.searchLabel ?? (typeof opt.name === "string" ? opt.name : opt.id),
        searchLabel: opt.searchLabel,
      })),
    [selectedOptions],
  );

  const filteredOptions: MultiSelectComboBoxOption[] = useMemo(() => {
    if (externalSearch || !withSearch || !searchTerm) return options;

    const lowerSearch = searchTerm.toLowerCase();
    return options.filter((opt) => {
      const searchText = opt.searchLabel ?? (typeof opt.name === "string" ? opt.name : opt.id);
      return searchText.toLowerCase().includes(lowerSearch);
    });
  }, [options, withSearch, searchTerm, externalSearch]);

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      externalSearch?.(term);
    },
    [externalSearch],
  );

  const updateSelection = useCallback(
    (newOptions: MultiSelectComboBoxOption[]) => {
      if (!isControlled) {
        setInternalValue(newOptions);
      }
      onChange?.(newOptions);
    },
    [isControlled, onChange],
  );

  const handleTagRemove = useCallback(
    (items: MultiSelectFieldItem[]) => {
      const remainingIds = new Set(items.map((i) => i.id));
      updateSelection(selectedOptions.filter((o) => remainingIds.has(o.id)));
    },
    [selectedOptions, updateSelection],
  );

  const handleOpenChange = (open: boolean) => {
    if (isReadOnly && open) return;

    setIsOpen(open);
    if (!open) {
      setSearchTerm("");
      externalSearch?.("");
    }
  };

  return (
    <MultiSelectDropdown
      isOpen={isOpen}
      label={label ?? ""}
      onChange={updateSelection}
      onOpenChange={handleOpenChange}
      onSearch={withSearch ? handleSearch : undefined}
      options={filteredOptions}
      placement={placement}
      searchPlaceholder={searchPlaceholder}
      searchValue={searchTerm}
      trigger={
        <MultiSelectField
          assistiveText={assistiveText}
          className={className}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          isOpen={isOpen}
          isReadOnly={isReadOnly}
          isRequired={isRequired}
          label={label}
          onChange={handleTagRemove}
          placeholder={placeholder}
          ref={ref}
          showClearAll={showClearAll}
          tooltipText={tooltipText}
          value={selectedItems}
        />
      }
      value={selectedOptions}
      matchTriggerWidth
    />
  );
}
